import { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ChatContextProvider } from '../context/chat-context';
import { CreateEndionChatbotOptions } from '../endion-chatbot';
import { DecodedApiError, useParseApiError } from '../hooks/parse-api-error.hook';
import { useSetVisitedPropertyUrlToSessionStorage } from '../hooks/real-estate/use-set-visited-property-url-to-session-storage.hook';
import { useChatOpenState } from '../hooks/use-chat-open-state.hook';
import { useChat } from '../hooks/use-chat.hook';
import { useChatbotTriggerState } from '../hooks/use-chatbot-trigger-state.hook';
import { useCloseConversation } from '../hooks/use-close-conversation.hook';
import { useCustomerChatbotContext } from '../hooks/use-customer-chatbot-context';
import { useFindConversation } from '../hooks/use-find-conversation.hook';
import { CHAT_VIEWPORT_CLASSNAME, useSetStyle } from '../hooks/use-set-style.hook';
import { useValidateChatInput } from '../hooks/validate-chat-input.hook';
import { clearConversationKeyFromBrowserStorage } from '../utils/local-storage';
import { BotTriggerButton } from './bot-trigger-button';
import { ChatMessageList } from './chat-message/chat-message-list';
import { ChatViewport } from './chat-viewport';
import { ChatbotFooter } from './chatbot-footer';
import { ChatInputForm } from './inputs/chat-input-form';

type ChatProps = {
  replaceDefaultClickBehaviorWithLink?: CreateEndionChatbotOptions['replaceDefaultClickBehaviorWithLink'];
};

export function Chat({ replaceDefaultClickBehaviorWithLink }: ChatProps) {
  const {
    customerChatbot: chatCustomerContext,
    isLoading: isLoadingContext,
    chatbotApiKey,
    showTrigger: initialShowTrigger,
  } = useCustomerChatbotContext();
  // TODO: analyze if isLoading is necessary
  const {
    isLoading: isLoadingConversation,
    storedConversationHistory,
    error: findConversationApiError,
    findConversation,
  } = useFindConversation({ chatbotApiKey });

  const { parseApiError, decodedApiError, setDecodedApiError } = useParseApiError();

  // Chatbot open/close state with external control support
  const { isOpen, setIsOpen } = useChatOpenState();

  // Chatbot trigger visibility state with external control support
  const { showTrigger } = useChatbotTriggerState(initialShowTrigger);

  // HTML refs
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    input,
    setInput,
    setMessages,
    append,
    handleInputChange,
    messages,
    isLoading,
    defaultAssistanMessage,
    submit,
  } = useChat({ conversationHistory: storedConversationHistory, parseApiError, inputRef });

  const { error: messageInputError, setError: setMessageInputError } = useValidateChatInput({ input });

  // Parse incoming errors from find conversation
  useEffect(() => {
    if (findConversationApiError) {
      parseApiError(findConversationApiError);
    }
  }, [findConversationApiError, parseApiError]);

  // Evaluate if the current conversation is closed and we need to display the Reset button with a specific error message
  const { closeReasonMessage, setCloseReasonMessage } = useCloseConversation({ messages, decodedApiError });

  async function resetChat(rollback?: boolean) {
    setCloseReasonMessage(null);
    setDecodedApiError(null);
    setMessageInputError(null);
    // There are two ways to reset the conversation.
    // A full reset, which clears the conversation from the storage to start a new one,
    // or a rollback, which roll-backs the conversation interactions that have caused a streaming error.
    if (rollback) {
      // Get the last user message
      const lastUserMessageContent = [...messages].reverse().find((m) => m.role === 'user')?.content;
      if (!lastUserMessageContent) return;
      const lastUserMessage = 'text' in lastUserMessageContent[0] ? lastUserMessageContent[0].text : undefined;
      setInput(lastUserMessage || '');

      // Retrieve the conversation again. At this point it was rolled-back on the server.
      await findConversation();

      // Set the focus back to the input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    } else {
      // Start a new fresh conversation
      setMessages([defaultAssistanMessage]);
      clearConversationKeyFromBrowserStorage();
    }
  }

  useSetVisitedPropertyUrlToSessionStorage();
  useSetStyle({ style: chatCustomerContext?.style });

  // If the chat is not active, the chat UI is not rendered
  if (!chatCustomerContext?.isActive) return null;

  return (
    <ChatContextProvider
      isOpen={isOpen}
      storedConversationHistory={storedConversationHistory}
      setMessages={setMessages}
      messages={messages}
      submit={submit}
    >
      {showTrigger && (
        <BotTriggerButton
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          style={chatCustomerContext.style}
          replaceDefaultClickBehaviorWithLink={replaceDefaultClickBehaviorWithLink}
        />
      )}
      <ChatViewport
        className={`${
          isOpen ? 'flex flex-col opacity-100' : 'invisible opacity-0 size-0'
        } fixed bottom-0 ${CHAT_VIEWPORT_CLASSNAME} sm:w-[28rem] sm:h-[42rem] sm:max-h-[80%] w-full h-dvh bg-backgroundColor rounded-lg shadow-[0px_0px_16px_-4px_rgba(0,0,0,0.2)] transition-opacity duration-300 `}
      >
        <header className="py-5 flex justify-between px-6 items-center border-b border-b-borderColor rounded-t-lg">
          <div className="flex flex-col">
            <h3 className="text-textColor self-center font-medium text-lg">Mensajes</h3>
          </div>

          <XMarkIcon height={24} className="text-textColor cursor-pointer" onClick={() => setIsOpen(false)} />
        </header>
        <ChatMessageList messages={messages} isLoading={isLoading} isLoadingContext={isLoadingContext} />
        <ErrorContainer error={messageInputError} />
        <ErrorContainer error={decodedApiError?.message} />
        <ChatInputContainer
          {...{
            closeReasonMessage,
            decodedApiError,
            resetChat,
          }}
        >
          <ChatInputForm
            {...{
              handleInputChange,
              error: messageInputError,
              append,
              input,
              isLoading: isLoading || isLoadingContext || isLoadingConversation,
              ref: inputRef,
              setInput,
            }}
          />
        </ChatInputContainer>
        <ChatbotFooter />
      </ChatViewport>
    </ChatContextProvider>
  );
}

function ChatInputContainer({
  children,
  closeReasonMessage,
  decodedApiError,
  resetChat,
}: {
  children: React.ReactNode;
  decodedApiError: DecodedApiError | null;
  resetChat: (rollback?: boolean) => void;
  closeReasonMessage: string | null;
}) {
  if (closeReasonMessage) return <ResetChatButtonContainer {...{ resetChat, closeReasonMessage, decodedApiError }} />;
  return <div className="flex border border-t">{children}</div>;
}

function ErrorContainer({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return <span className="text-errorColor text-xs p-2">{error}</span>;
}

function ResetChatButtonContainer({
  closeReasonMessage,
  resetChat,
  decodedApiError,
}: {
  closeReasonMessage: string | null;
  decodedApiError: DecodedApiError | null;
  resetChat: (rollback?: boolean) => void;
}) {
  if (closeReasonMessage === null) return null;

  const shouldRollbackConversation =
    decodedApiError?.code === 'STREAMING_ERROR' || decodedApiError?.code === 'LLM_ERROR';

  const handleResetButtonClick = () => {
    resetChat(shouldRollbackConversation);
  };

  return (
    <div className="flex flex-col space-y-2 text-center border border-t p-2 justify-center">
      <h5 className="font-medium px-2">{closeReasonMessage}</h5>
      <button className="p-2 rounded text-white bg-primary" onClick={handleResetButtonClick}>
        {shouldRollbackConversation ? 'Intentar nuevamente' : 'Reiniciar conversación'}
      </button>
    </div>
  );
}
